import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

const STYLES = `
  .clinical-markdown {
    color: inherit;
    font-size: inherit;
    line-height: 1.72;
    overflow-wrap: anywhere;
  }

  .clinical-markdown > :first-child { margin-top: 0; }
  .clinical-markdown > :last-child { margin-bottom: 0; }

  .clinical-markdown h1,
  .clinical-markdown h2,
  .clinical-markdown h3,
  .clinical-markdown h4 {
    margin: 18px 0 8px;
    color: #0F2742;
    font-weight: 700;
    line-height: 1.35;
    text-wrap: balance;
  }

  .clinical-markdown h1 { font-size: 20px; }
  .clinical-markdown h2 { font-size: 18px; }
  .clinical-markdown h3 { font-size: 16px; }
  .clinical-markdown h4 { font-size: 14px; }
  .clinical-markdown p { margin: 0 0 9px; }
  .clinical-markdown ul,
  .clinical-markdown ol { margin: 6px 0 10px; padding-inline-start: 24px; }
  .clinical-markdown li { margin: 3px 0; padding-inline-start: 2px; }
  .clinical-markdown li > p { margin: 0; }

  .clinical-markdown blockquote {
    margin: 10px 0;
    padding: 8px 12px;
    border-inline-start: 3px solid #9AB4D0;
    background: #F3F7FB;
    color: #46607A;
  }

  .clinical-markdown__table-scroll {
    width: 100%;
    margin: 10px 0;
    overflow-x: auto;
    border: 1px solid #DCE6F0;
    border-radius: 8px;
    -webkit-overflow-scrolling: touch;
  }

  .clinical-markdown table {
    width: 100%;
    min-width: 480px;
    border-collapse: collapse;
    background: #FFFFFF;
  }

  .clinical-markdown th,
  .clinical-markdown td {
    padding: 8px 10px;
    border-bottom: 1px solid #E7EDF5;
    text-align: left;
    vertical-align: top;
  }

  .clinical-markdown th {
    background: #F3F7FB;
    color: #24415F;
    font-weight: 650;
    white-space: nowrap;
  }

  .clinical-markdown tr:last-child td { border-bottom: 0; }
  .clinical-markdown a { color: #0B63CE; text-decoration: underline; text-underline-offset: 2px; }
  .clinical-markdown a:focus-visible { outline: 2px solid #0B63CE; outline-offset: 2px; border-radius: 2px; }

  .clinical-markdown code {
    border-radius: 4px;
    background: #EAF0F6;
    padding: 1px 4px;
    font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
    font-size: 0.92em;
  }

  .clinical-markdown pre {
    margin: 10px 0;
    overflow-x: auto;
    border-radius: 8px;
    background: #10263D;
    padding: 12px;
    color: #F6F9FC;
  }

  .clinical-markdown pre code { background: transparent; padding: 0; color: inherit; }
  .clinical-markdown img { max-width: 100%; height: auto; }

  @media (max-width: 768px) {
    .clinical-markdown { font-size: 15px; line-height: 1.68; }
    .clinical-markdown h1 { font-size: 19px; }
    .clinical-markdown h2 { font-size: 17px; }
    .clinical-markdown h3 { font-size: 16px; }
    .clinical-markdown ul,
    .clinical-markdown ol { padding-inline-start: 21px; }
  }
`;

export const normalizeClinicalMarkdown = (value: string): string => {
  const normalizedLineEndings = String(value || '').replace(/\r\n?/g, '\n');
  if (!normalizedLineEndings.includes('\n') && normalizedLineEndings.includes('\\n')) {
    return normalizedLineEndings.replace(/\\n/g, '\n');
  }
  return normalizedLineEndings;
};

interface ClinicalMarkdownProps {
  children: string;
  className?: string;
}

const ClinicalMarkdown: React.FC<ClinicalMarkdownProps> = ({ children, className }) => (
  <>
    <style>{STYLES}</style>
    <div className={`clinical-markdown${className ? ` ${className}` : ''}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        skipHtml
        components={{
          a: ({ node: _node, ...props }) => (
            <a {...props} target="_blank" rel="noreferrer noopener" />
          ),
          table: ({ node: _node, ...props }) => (
            <div className="clinical-markdown__table-scroll" role="region" aria-label="临床解读表格" tabIndex={0}>
              <table {...props} />
            </div>
          ),
        }}
      >
        {normalizeClinicalMarkdown(children)}
      </ReactMarkdown>
    </div>
  </>
);

export default ClinicalMarkdown;
